import React, { memo, useState, useCallback, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Box,
  Divider,
  Chip,
  Paper,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const defaultSettings = {
  leave: false,
  custom: false,
  all: false,
  reportType: "details",
  customFields: [],
  showStartingBalance: false,
  showBonus: false,
};
// recieves 3 things from allAttendanceNewPage
function ReportSettingsLauncher({
  onApply, //apply click 
  gridFields = [],  //list of all available field names for custom panel
  initialSettings = defaultSettings, //saved settings from localStorage
}) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({
    ...defaultSettings,
    ...initialSettings,
    customFields: Array.isArray(initialSettings?.customFields)
      ? initialSettings.customFields
      : [],
  });
// Sync when initialSettings changes, keeps the dialog in sync with what's saved in localStorage.
  useEffect(() => {
    setSettings({
      ...defaultSettings,
      ...initialSettings,
      customFields: Array.isArray(initialSettings?.customFields)
        ? initialSettings.customFields
        : [],
    });
  }, [initialSettings]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggle = useCallback(
    (key) => (event) => {
      const checked = event.target.checked;
      //when user clicks any checkbox
      setSettings((prev) => {
        if (key === "leave") {
          return {
            ...prev,
            leave: checked,  // turn Leave on or off
            custom: checked ? false : prev.custom,  // if turning Leave ON → turn Custom OFF
            all: false,  // always turn off "Include All"
          };
        }

        if (key === "custom") {
          return {
            ...prev,
            custom: checked,
            leave: checked ? false : prev.leave,
            all: false,
            customFields: checked ? prev.customFields : [],
          };
        }

        if (key === "all") {
          return {
            ...prev,
            all: checked,
            leave: checked ? false : prev.leave, //turn off leave
            custom: checked ? false : prev.custom,  // turn off custom 
            customFields: checked ? gridFields.map((f) => f.key) : prev.customFields,
            // if Include All ON → auto-check ALL fields in the Custom Fields panel
          };
        }
      // For any other checkbox (showBonus, showStartingBalance) — just toggle it directly.
        return {
          ...prev,
          [key]: checked,
        };
      });
    },
    [gridFields]
  );

  const handleReportTypeChange = useCallback((event) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      reportType: value,
    }));
  }, []);

  const handleCustomFieldToggle = useCallback(
    (fieldKey) => (event) => { // fieldKey = custom fields , attendance, leave ,..
      const checked = event.target.checked;

      setSettings((prev) => {
        const exists = prev.customFields.includes(fieldKey);

        let nextFields = prev.customFields;

        if (checked && !exists) {
          nextFields = [...prev.customFields, fieldKey];
        } else if (!checked && exists) {
          nextFields = prev.customFields.filter((key) => key !== fieldKey);
        }

        return {
          ...prev,
          customFields: nextFields,
        };
      });
    },
    []
  );

  const handleApply = useCallback(() => {
    if (typeof onApply === "function") {
      onApply(settings); // send settings back to AllAttendanceNewPage
    }
    setOpen(false);
  }, [onApply, settings]);

  const selectedFieldCount =
    settings.all ? gridFields.length : settings.customFields.length;
// Include All" is on → show total fields count. Otherwise → show how many custom fields are checked.
  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<SettingsIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 800,
          borderRadius: 3,
          px: 2.5,
          py: 1.15,
          minHeight: 46,
          boxShadow: "0 10px 24px rgba(25,118,210,0.18)",
          background: "linear-gradient(135deg, #7b1fa2 0%, #1976d2 100%)",
        }}
      >
        Report Settings
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: "hidden",
            boxShadow: "0 24px 80px rgba(15,23,42,0.18)",
            background:
              "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 0,
            py: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(123,31,162,0.10) 0%, rgba(25,118,210,0.08) 100%)",
          }}
        >
          <Box sx={{ px: 3, py: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    background: "linear-gradient(135deg, #7b1fa2 0%, #1976d2 100%)",
                    boxShadow: "0 12px 24px rgba(25,118,210,0.22)",
                  }}
                >
                  <DescriptionOutlinedIcon />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 900,
                      lineHeight: 1.2,
                      letterSpacing: 0.2,
                    }}
                  >
                    Report Configuration
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    Configure report type, visible data, and optional payroll fields.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Chip
                  label={settings.reportType === "details" ? "Detailed" : "Summary"}
                  color="primary"
                  variant="filled"
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                />
                <Chip
                  label={`${selectedFieldCount} fields selected`}
                  variant="outlined"
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                />
              </Stack>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.05fr 1fr" },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: "divider",
                  background:
                    "linear-gradient(180deg, rgba(123,31,162,0.03) 0%, rgba(123,31,162,0.01) 100%)",
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(123,31,162,0.12)",
                      color: "secondary.main",
                    }}
                  >
                    <TuneIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                      Report Structure
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Select the report mode and the data scope.
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ mb: 2.5 }} />

                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, mb: 1.25, fontSize: 15 }}
                >
                  Report Type
                </Typography>

                <RadioGroup
                  value={settings.reportType}
                  onChange={handleReportTypeChange}
                  sx={{ mb: 2.5 }}
                >
                  <FormControlLabel
                    value="details"
                    control={<Radio />}
                    label="Detailed Report"
                  />
                  <FormControlLabel
                    value="summary"
                    control={<Radio />}
                    label="Summary Report"
                  />
                </RadioGroup>

                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, mb: 1.25, fontSize: 15 }}
                >
                  Report Sections
                </Typography>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.leave}
                        onChange={handleToggle("leave")}
                      />
                    }
                    label="Leave Data"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.custom}
                        onChange={handleToggle("custom")}
                      />
                    }
                    label="Custom Selection"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.all}
                        onChange={handleToggle("all")}
                      />
                    }
                    label="Include All Fields"
                  />
                </FormGroup>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: "divider",
                  background:
                    "linear-gradient(180deg, rgba(25,118,210,0.03) 0%, rgba(25,118,210,0.01) 100%)",
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(25,118,210,0.12)",
                      color: "primary.main",
                    }}
                  >
                    <PaymentsIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                      Additional Financial Options
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Show optional balance and bonus values in the report.
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ mb: 2.5 }} />

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.showStartingBalance}
                        onChange={handleToggle("showStartingBalance")}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountBalanceWalletIcon fontSize="small" />
                        <span>Show Starting Balance</span>
                      </Stack>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.showBonus}
                        onChange={handleToggle("showBonus")}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PaymentsIcon fontSize="small" />
                        <span>Show Bonus</span>
                      </Stack>
                    }
                  />
                </FormGroup>
              </Paper>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                background: "#ffffff",
                minHeight: 100,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(16,185,129,0.12)",
                    color: "success.main",
                  }}
                >
                  <ViewListIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                    Custom Fields
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Choose which grid columns should appear in the report.
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 2.5 }} />

              <Box
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: "rgba(25,118,210,0.04)",
                  border: "1px dashed",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {settings.custom || settings.all
                    ? "You can select custom fields below."
                    : "Enable Custom Selection or Include All Fields to manage field visibility."}
                </Typography>
              </Box>

              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  p: 1.5,
                  bgcolor: "#fafafa",
                }}
              >
                <FormGroup>
                  {gridFields.length > 0 ? (
                    gridFields.map((field) => (
                      <Box
                        key={field.key}
                        sx={{
                          px: 1,
                          py: 0.35,
                          borderRadius: 2,
                          "&:hover": {
                            bgcolor: "rgba(25,118,210,0.05)",
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={settings.customFields.includes(field.key)}
                              onChange={handleCustomFieldToggle(field.key)}
                              disabled={!settings.custom && !settings.all}
                            />
                          }
                          label={field.label}
                        />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No grid fields found.
                    </Typography>
                  )}
                </FormGroup>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.25,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
            background: "rgba(15,23,42,0.02)",
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2.5,
              px: 2.25,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2.5,
              px: 3.25,
              py: 1,
              boxShadow: "0 10px 24px rgba(25,118,210,0.18)",
              background: "linear-gradient(135deg, #7b1fa2 0%, #1976d2 100%)",
            }}
          >
            Apply Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(ReportSettingsLauncher);